import React, { useEffect, useState } from "react";
import api from "../api/client";
import { Card, PageHeader } from "../components/ui";

const fmt = (v) => v != null ? "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : "\u2014";
const fmtPct = (v) => v != null ? v.toFixed(1) + "%" : "\u2014";
const fmtMoic = (v) => v != null ? v.toFixed(2) + "x" : "\u2014";

const StatusBadge = ({ status }) => {
  const colors = {
    "Fully Exited": { bg: "#dcfce7", color: "#166534" },
    "Partially Exited": { bg: "#fef9c3", color: "#854d0e" },
    "Open": { bg: "#dbeafe", color: "#1e40af" },
    "No Positions": { bg: "#f1f5f9", color: "#64748b" },
  };
  const c = colors[status] || colors["No Positions"];
  return <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
};

const KPI = ({ label, value, sub, color }) => (
  <div style={{ flex: 1, minWidth: 160, padding: "16px 20px", background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0" }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || "#1e293b" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
);

export default function CashWaterfall() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealDetail, setDealDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("cash_out");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    api.get("/market-data/waterfall/").then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadDealDetail = (dealId) => {
    setSelectedDeal(dealId);
    api.get(`/market-data/waterfall/${dealId}/`).then(r => setDealDetail(r.data));
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading waterfall data...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: "center", color: "#ef4444" }}>Failed to load waterfall data</div>;

  const s = data.summary;
  const deals = data.deals || [];
  const filtered = filter === "all" ? deals
    : filter === "exited" ? deals.filter(d => d.waterfall_status === "Fully Exited")
    : filter === "open" ? deals.filter(d => d.waterfall_status === "Open" || d.waterfall_status === "Partially Exited")
    : filter === "winners" ? deals.filter(d => d.realized_pnl > 0)
    : filter === "losers" ? deals.filter(d => d.realized_pnl < 0 && d.cash_out > 0)
    : deals;
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortBy] || 0, bv = b[sortBy] || 0;
    return sortDir === "desc" ? bv - av : av - bv;
  });
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };
  const arrow = (col) => sortBy === col ? (sortDir === "desc" ? " \u25BC" : " \u25B2") : "";

  return (
    <div>
      <PageHeader title="Cash Waterfall" subtitle={`${s.total_deals} deals \u2014 ${s.fully_exited} exited, ${s.open + s.partially_exited} active`} />

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <KPI label="Capital Deployed" value={fmt(s.total_invested)} sub={`across ${s.total_deals} deals`} />
        <KPI label="Cash Returned" value={fmt(s.total_returned)} sub="realized proceeds" />
        <KPI label="Realized P&L" value={fmt(s.realized_pnl)} color={s.realized_pnl >= 0 ? "#2d6a4f" : "#9b2226"} sub={fmtPct(s.total_invested > 0 ? (s.realized_pnl / s.total_invested) * 100 : 0) + " return on capital"} />
        <KPI label="Fund MOIC" value={fmtMoic(s.fund_moic_realized)} color="#345479" sub={"Total: " + fmtMoic(s.fund_moic_total) + " (incl. unrealized)"} />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <KPI label="Unrealized P&L" value={fmt(s.unrealized_pnl)} color={s.unrealized_pnl >= 0 ? "#2d6a4f" : "#9b2226"} />
        <KPI label="Open Market Value" value={fmt(s.open_market_value)} sub="current mark on open positions" />
        <KPI label="Total Return" value={fmt(s.total_return)} sub="realized + open market value" color="#345479" />
        <KPI label="Net Cash Flow" value={fmt(s.net_cash)} color={s.net_cash >= 0 ? "#2d6a4f" : "#9b2226"} sub="cash returned minus deployed" />
      </div>

      <Card>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Returns by Strategy</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {(data.by_strategy || []).map((st, i) => (
            <div key={i} style={{ padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 6 }}>{st.strategy}</div>
              <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.8 }}>
                <div>{st.deal_count} deal{st.deal_count !== 1 ? "s" : ""} &mdash; {fmt(st.cash_out)} deployed</div>
                <div>Returned: {fmt(st.cash_in)} &mdash; MOIC: <strong style={{ color: st.moic >= 1 ? "#2d6a4f" : "#9b2226" }}>{fmtMoic(st.moic)}</strong></div>
                <div>P&L: <span style={{ color: st.realized_pnl >= 0 ? "#2d6a4f" : "#9b2226", fontWeight: 600 }}>{fmt(st.realized_pnl)}</span></div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", gap: 6, margin: "16px 0" }}>
        {[{ key: "all", label: "All Deals" }, { key: "exited", label: "Fully Exited" }, { key: "open", label: "Active" }, { key: "winners", label: "Winners" }, { key: "losers", label: "Losers" }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: filter === f.key ? "#345479" : "#fff", color: filter === f.key ? "#fff" : "#334155", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>{f.label}</button>
        ))}
      </div>

      <Card>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600 }}>#</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600 }}>Deal</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600 }}>Strategy</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600 }}>Status</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right", cursor: "pointer" }} onClick={() => handleSort("cash_out")}>Cash Out{arrow("cash_out")}</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right", cursor: "pointer" }} onClick={() => handleSort("cash_in")}>Cash In{arrow("cash_in")}</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right", cursor: "pointer" }} onClick={() => handleSort("realized_pnl")}>Realized P&L{arrow("realized_pnl")}</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right", cursor: "pointer" }} onClick={() => handleSort("moic_realized")}>MOIC{arrow("moic_realized")}</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right", cursor: "pointer" }} onClick={() => handleSort("irr")}>IRR{arrow("irr")}</th>
                <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 600, textAlign: "right" }}>Holding</th>
              </tr>
            </thead>
            <tbody>
              {sorted.filter(d => d.total_lots > 0).map((d, i) => (
                <tr key={d.deal_id} onClick={() => loadDealDetail(d.deal_id)}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", background: selectedDeal === d.deal_id ? "#f0f9ff" : i % 2 === 0 ? "#fff" : "#fafafa" }}
                    onMouseOver={e => { e.currentTarget.style.background = "#f0f9ff"; }}
                    onMouseOut={e => { e.currentTarget.style.background = selectedDeal === d.deal_id ? "#f0f9ff" : i % 2 === 0 ? "#fff" : "#fafafa"; }}>
                  <td style={{ padding: "10px 8px", color: "#94a3b8" }}>{i + 1}</td>
                  <td style={{ padding: "10px 8px", fontWeight: 600, color: "#1e293b" }}>{d.deal_name}</td>
                  <td style={{ padding: "10px 8px", color: "#64748b" }}>{d.strategy_tag || "\u2014"}</td>
                  <td style={{ padding: "10px 8px" }}><StatusBadge status={d.waterfall_status} /></td>
                  <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "monospace" }}>{fmt(d.cash_out)}</td>
                  <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "monospace" }}>{fmt(d.cash_in)}</td>
                  <td style={{ padding: "10px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: d.realized_pnl >= 0 ? "#2d6a4f" : "#9b2226" }}>{fmt(d.realized_pnl)}</td>
                  <td style={{ padding: "10px 8px", textAlign: "right", fontWeight: 700, color: (d.moic_realized || 0) >= 1 ? "#2d6a4f" : "#9b2226" }}>{fmtMoic(d.moic_realized)}</td>
                  <td style={{ padding: "10px 8px", textAlign: "right", color: (d.irr || 0) >= 0 ? "#2d6a4f" : "#9b2226" }}>{d.irr != null ? fmtPct(d.irr) : "\u2014"}</td>
                  <td style={{ padding: "10px 8px", textAlign: "right", color: "#64748b" }}>{d.holding_months != null ? d.holding_months + "mo" : "\u2014"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {dealDetail && (
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{dealDetail.deal_name}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{dealDetail.strategy_tag} &mdash; {dealDetail.ticker} &mdash; <StatusBadge status={dealDetail.waterfall_status} /></div>
            </div>
            <button onClick={() => { setSelectedDeal(null); setDealDetail(null); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", color: "#64748b", fontSize: 12, cursor: "pointer" }}>Close</button>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <KPI label="Invested" value={fmt(dealDetail.cash_out)} />
            <KPI label="Returned" value={fmt(dealDetail.cash_in)} />
            <KPI label="Realized P&L" value={fmt(dealDetail.realized_pnl)} color={dealDetail.realized_pnl >= 0 ? "#2d6a4f" : "#9b2226"} />
            <KPI label="MOIC" value={fmtMoic(dealDetail.moic_realized)} color={(dealDetail.moic_realized || 0) >= 1 ? "#2d6a4f" : "#9b2226"} />
            {dealDetail.irr != null && <KPI label="IRR" value={fmtPct(dealDetail.irr)} color={dealDetail.irr >= 0 ? "#2d6a4f" : "#9b2226"} />}
            {dealDetail.holding_months && <KPI label="Holding Period" value={dealDetail.holding_months + " months"} sub={dealDetail.holding_days + " days"} />}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 10 }}>Cash Flow Timeline</div>
          <div style={{ borderLeft: "3px solid #e2e8f0", paddingLeft: 20, marginLeft: 8 }}>
            {(dealDetail.events || []).map((ev, i) => (
              <div key={i} style={{ marginBottom: 12, position: "relative" }}>
                <div style={{ position: "absolute", left: -28, top: 4, width: 12, height: 12, borderRadius: "50%",
                  background: ev.type === "Funding" ? "#dc2626" : ev.type === "Exit" || ev.type === "Proceeds" ? "#059669" : ev.type === "Conversion" ? "#7c3aed" : "#f59e0b",
                  border: "2px solid #fff", boxShadow: "0 0 0 2px #e2e8f0" }} />
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>{ev.date || "N/A"}</div>
                <div style={{ fontSize: 12, color: "#1e293b" }}>
                  <span style={{ fontWeight: 600, color: ev.amount < 0 ? "#dc2626" : ev.amount > 0 ? "#059669" : "#7c3aed" }}>{ev.type}</span>
                  {" \u2014 "}{ev.description}
                </div>
              </div>
            ))}
          </div>
          {dealDetail.open_lots > 0 && (
            <div style={{ marginTop: 16, padding: 12, background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e40af" }}>{dealDetail.open_lots} open position{dealDetail.open_lots > 1 ? "s" : ""}</div>
              <div style={{ fontSize: 11, color: "#3b82f6", marginTop: 4 }}>Market Value: {fmt(dealDetail.open_market_value)} &mdash; Unrealized P&L: <strong>{fmt(dealDetail.unrealized_pnl)}</strong></div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
